import React, { useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Container,
  Typography,
  Box,
  CircularProgress,
  Paper,
  Alert,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  Tooltip,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Warning as RiskIcon,
  Security as MitigationIcon,
  CheckCircle as ImplementedIcon,
  Schedule as PlannedIcon,
  PlayArrow as InProgressIcon,
  VerifiedUser as VerifiedIcon,
  Language as LanguageIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import embedsService, {
  type ComponentWithRisks,
  type RiskWithMitigations,
} from '../services/embeds.service';
import { DiagramViewer, type ComponentData } from '../components/DiagramViewer';
import type { Diagram } from '../../../shared/src/types/space.types';
import { ISO_CONTROLS } from '../../../shared/src/catalogs/iso27001';
import { getD3fendUrl } from '../../../shared/src/catalogs/mitre-d3fend';
import { getAttackUrl } from '../../../shared/src/catalogs/mitre-attack';

// =============================================================================
// LANGUAGE TOGGLE COMPONENT FOR EMBED
// =============================================================================

const LanguageToggle: React.FC = () => {
  const { i18n } = useTranslation();

  const toggleLanguage = () => {
    const newLang = i18n.language === 'nb' ? 'en' : 'nb';
    i18n.changeLanguage(newLang);
  };

  const isNorwegian = i18n.language === 'nb' || i18n.language === 'no';

  return (
    <Tooltip title={isNorwegian ? 'Switch to English' : 'Bytt til norsk'}>
      <Chip
        icon={<LanguageIcon fontSize="small" />}
        label={isNorwegian ? 'English' : 'Norsk'}
        onClick={toggleLanguage}
        size="small"
        variant="outlined"
        sx={{
          cursor: 'pointer',
          '&:hover': {
            backgroundColor: 'action.hover',
          },
        }}
      />
    </Tooltip>
  );
};

/**
 * EmbedView - Public page for viewing embedded content
 * This page is accessed via /embed/:token route
 * No authentication required
 */
export const EmbedView: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);

  const {
    data: embedContent,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['embed-content', token],
    queryFn: () => embedsService.getPublicContent(token || ''),
    enabled: !!token,
    retry: false,
  });

  // Send height to parent window for iframe resizing
  useEffect(() => {
    const sendHeight = () => {
      if (containerRef.current && window.parent !== window) {
        const height = containerRef.current.scrollHeight;
        window.parent.postMessage({ type: 'embed-resize', height, token }, '*');
      }
    };

    // Only run when we have content
    if (!embedContent) return;

    // Send initial height after render
    const timer = setTimeout(sendHeight, 100);

    // Set up ResizeObserver to detect content changes
    const resizeObserver = new ResizeObserver(sendHeight);
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    // Also send on window resize
    window.addEventListener('resize', sendHeight);

    return () => {
      clearTimeout(timer);
      resizeObserver.disconnect();
      window.removeEventListener('resize', sendHeight);
    };
  }, [token, embedContent]);

  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          backgroundColor: '#f5f5f5',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error || !embedContent) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Alert severity="error">{t('embed.notFound')}</Alert>
      </Container>
    );
  }

  const { embed, content, componentsWithRisks } = embedContent;

  return (
    <Box
      ref={containerRef}
      sx={{
        backgroundColor: '#f5f5f5',
        p: 2,
      }}
    >
      <Paper sx={{ p: 3, borderRadius: 2 }}>
        {/* Header with language selector */}
        <Box
          sx={{
            mb: 3,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
          }}
        >
          <Box>
            <Typography variant="h5" component="h1" gutterBottom>
              {embed.name}
            </Typography>
            {embed.description && (
              <Typography variant="body2" color="text.secondary">
                {embed.description}
              </Typography>
            )}
          </Box>
          <LanguageToggle />
        </Box>

        {/* Content */}
        {embed.type === 'diagram' ? (
          <DiagramEmbedContent
            content={content}
            componentsWithRisks={componentsWithRisks}
            includeRisks={
              !!(componentsWithRisks && componentsWithRisks.length > 0)
            }
          />
        ) : (
          <ComponentEmbedContent
            content={content}
            componentsWithRisks={componentsWithRisks}
            includeRisks={
              !!(componentsWithRisks && componentsWithRisks.length > 0)
            }
          />
        )}

        {/* Footer */}
        <Box
          sx={{
            mt: 2,
            pt: 2,
            borderTop: 1,
            borderColor: 'divider',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography variant="caption" color="text.secondary">
            {t('embed.poweredBy')}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Risikostyring
          </Typography>
        </Box>
      </Paper>
    </Box>
  );
};

// =============================================================================
// DIAGRAM EMBED CONTENT
// =============================================================================

interface DiagramEmbedContentProps {
  content: unknown;
  componentsWithRisks?: ComponentWithRisks[];
  includeRisks?: boolean;
}

const DiagramEmbedContent: React.FC<DiagramEmbedContentProps> = ({
  content,
  componentsWithRisks,
  includeRisks,
}) => {
  const { t } = useTranslation();
  const diagramData = content as Diagram | null;

  if (!diagramData) {
    return <Alert severity="info">{t('embed.noDiagramData')}</Alert>;
  }

  // Convert componentsWithRisks to the format expected by DiagramViewer
  const componentData: ComponentData[] = (componentsWithRisks || []).map(
    comp => ({
      id: comp.id,
      name: comp.name,
      type: comp.type,
      description: comp.description,
      confidentialityScore: comp.confidentialityScore,
      integrityScore: comp.integrityScore,
      availabilityScore: comp.availabilityScore,
      risks: comp.risks?.map(risk => ({
        id: risk.id,
        title: risk.title,
        inherentRisk: risk.inherentRisk,
        residualRisk: risk.residualRisk,
        mitigations: risk.mitigations?.map(mit => ({
          id: mit.id,
          title: mit.title,
          status: mit.implementationStatus || 'planned',
        })),
      })),
    })
  );

  return (
    <Box>
      {/* Interactive Diagram in Read-Only Mode */}
      <DiagramViewer
        diagram={diagramData}
        components={componentData}
        height={500}
        showControls={true}
        showMinimap={false}
      />

      {/* Hierarchical Component → Risk → Mitigation View */}
      {includeRisks &&
        componentsWithRisks &&
        componentsWithRisks.length > 0 && (
          <Box sx={{ mt: 3 }}>
            <Divider sx={{ mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              {t('embed.riskOverview')}
            </Typography>
            <RiskMatrix components={componentsWithRisks} />
            <ComponentRiskHierarchy components={componentsWithRisks} />
          </Box>
        )}
    </Box>
  );
};

// =============================================================================
// COMPONENT EMBED CONTENT
// =============================================================================

interface ComponentEmbedContentProps {
  content: unknown;
  componentsWithRisks?: ComponentWithRisks[];
  includeRisks?: boolean;
}

const ComponentEmbedContent: React.FC<ComponentEmbedContentProps> = ({
  content,
  componentsWithRisks,
  includeRisks,
}) => {
  const { t } = useTranslation();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const componentData = content as any;

  if (!componentData) {
    return <Alert severity="info">{t('embed.noComponentData')}</Alert>;
  }

  return (
    <Box sx={{ backgroundColor: 'grey.50', borderRadius: 1, p: 2 }}>
      <Typography variant="h6" gutterBottom>
        {componentData.name}
      </Typography>
      {componentData.description && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {componentData.description}
        </Typography>
      )}

      {/* Classification Scores */}
      <CIAScoreDisplay component={componentData} />

      {/* Risks and Mitigations */}
      {includeRisks &&
        componentsWithRisks &&
        componentsWithRisks.length > 0 && (
          <Box sx={{ mt: 3 }}>
            <Divider sx={{ mb: 2 }} />
            <RiskMatrix components={componentsWithRisks} />
            <ComponentRiskHierarchy components={componentsWithRisks} />
          </Box>
        )}
    </Box>
  );
};

// =============================================================================
// RISK MATRIX 5x5
// =============================================================================

interface RiskMatrixProps {
  components: ComponentWithRisks[];
}

const RiskMatrix: React.FC<RiskMatrixProps> = ({ components }) => {
  const { t } = useTranslation();

  // Collect all risks with their impact (average CIA) and likelihood
  const allRisks = components.flatMap(component =>
    (component.risks || []).map(risk => {
      // Calculate average impact from CIA scores or use inherentRisk as proxy
      const avgImpact =
        ((risk.confidentialityImpact || 0) +
          (risk.integrityImpact || 0) +
          (risk.availabilityImpact || 0)) /
          3 || Number(risk.inherentRisk || 0);

      return {
        id: risk.id,
        title: risk.title,
        componentName: component.name,
        impact: avgImpact,
        likelihood: risk.likelihood || 5, // Default to 5 if not set
        inherentRisk: Number(risk.inherentRisk || 0),
      };
    })
  );

  if (allRisks.length === 0) {
    return null;
  }

  // Standard best practice risk colors
  const MATRIX_COLORS = {
    critical: '#B71C1C', // Dark red
    high: '#E65100', // Orange
    medium: '#F9A825', // Yellow/amber
    low: '#2E7D32', // Green
    minimal: '#2E7D32', // Green
  };

  // Map score (1-10) to matrix position (0-4)
  const scoreToPosition = (score: number): number => {
    if (score >= 8) return 4;
    if (score >= 6) return 3;
    if (score >= 4) return 2;
    if (score >= 2) return 1;
    return 0;
  };

  // Get cell color based on position (impact col, likelihood row)
  const getCellColor = (impactPos: number, likelihoodPos: number): string => {
    const riskLevel = impactPos + likelihoodPos;
    if (riskLevel >= 7) return MATRIX_COLORS.critical;
    if (riskLevel >= 5) return MATRIX_COLORS.high;
    if (riskLevel >= 3) return MATRIX_COLORS.medium;
    return MATRIX_COLORS.low;
  };

  // Build matrix grid (5x5)
  const matrix: Array<Array<typeof allRisks>> = Array(5)
    .fill(null)
    .map(() =>
      Array(5)
        .fill(null)
        .map(() => [])
    );

  allRisks.forEach(risk => {
    const impactPos = scoreToPosition(risk.impact);
    const likelihoodPos = scoreToPosition(risk.likelihood);
    // Reverse likelihood so high is at top (row 0 = high likelihood)
    const row = 4 - likelihoodPos;
    matrix[row][impactPos].push(risk);
  });

  // Labels
  const impactLabels = [
    t('embed.matrix.veryLow'),
    t('embed.matrix.low'),
    t('embed.matrix.medium'),
    t('embed.matrix.high'),
    t('embed.matrix.veryHigh'),
  ];
  const likelihoodLabels = [
    t('embed.matrix.veryHigh'),
    t('embed.matrix.high'),
    t('embed.matrix.medium'),
    t('embed.matrix.low'),
    t('embed.matrix.veryLow'),
  ];

  return (
    <Box sx={{ mt: 2, mb: 3 }}>
      <Typography variant="subtitle2" fontWeight="medium" gutterBottom>
        {t('embed.riskMatrix')}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'stretch' }}>
        {/* Y-axis label */}
        <Box
          sx={{
            writingMode: 'vertical-rl',
            transform: 'rotate(180deg)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            pr: 0.5,
          }}
        >
          <Typography variant="caption" color="text.secondary">
            {t('embed.matrix.likelihood')}
          </Typography>
        </Box>

        <Box sx={{ flex: 1 }}>
          {/* Matrix grid */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'auto repeat(5, 1fr)',
              gap: '2px',
            }}
          >
            {/* Empty top-left corner */}
            <Box sx={{ width: 60 }} />

            {/* Impact header row */}
            {impactLabels.map((label, i) => (
              <Box
                key={`impact-${i}`}
                sx={{
                  backgroundColor: 'grey.200',
                  p: 0.5,
                  textAlign: 'center',
                  fontSize: '0.65rem',
                  fontWeight: 'medium',
                }}
              >
                {label}
              </Box>
            ))}

            {/* Matrix rows */}
            {matrix.map((row, rowIdx) => (
              <React.Fragment key={`row-${rowIdx}`}>
                {/* Likelihood label */}
                <Box
                  sx={{
                    backgroundColor: 'grey.200',
                    p: 0.5,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '0.65rem',
                    fontWeight: 'medium',
                    width: 60,
                  }}
                >
                  {likelihoodLabels[rowIdx]}
                </Box>

                {/* Matrix cells */}
                {row.map((cellRisks, colIdx) => {
                  const likelihoodPos = 4 - rowIdx; // Reverse for color calc
                  const bgColor = getCellColor(colIdx, likelihoodPos);
                  const count = cellRisks.length;

                  return (
                    <Tooltip
                      key={`cell-${rowIdx}-${colIdx}`}
                      title={
                        count > 0
                          ? cellRisks
                              .map(r => `${r.componentName}: ${r.title}`)
                              .join('\n')
                          : ''
                      }
                      arrow
                    >
                      <Box
                        sx={{
                          backgroundColor: bgColor,
                          opacity: count > 0 ? 1 : 0.3,
                          minHeight: 36,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'white',
                          fontWeight: 'bold',
                          fontSize: '0.875rem',
                          cursor: count > 0 ? 'pointer' : 'default',
                        }}
                      >
                        {count > 0 ? count : ''}
                      </Box>
                    </Tooltip>
                  );
                })}
              </React.Fragment>
            ))}
          </Box>

          {/* X-axis label */}
          <Box sx={{ textAlign: 'center', mt: 0.5 }}>
            <Typography variant="caption" color="text.secondary">
              {t('embed.matrix.impact')}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Legend */}
      <Box sx={{ display: 'flex', gap: 2, mt: 1.5, flexWrap: 'wrap' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Box
            sx={{
              width: 14,
              height: 14,
              backgroundColor: MATRIX_COLORS.critical,
              borderRadius: 0.5,
            }}
          />
          <Typography variant="caption">{t('risk.level.critical')}</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Box
            sx={{
              width: 14,
              height: 14,
              backgroundColor: MATRIX_COLORS.high,
              borderRadius: 0.5,
            }}
          />
          <Typography variant="caption">{t('risk.level.high')}</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Box
            sx={{
              width: 14,
              height: 14,
              backgroundColor: MATRIX_COLORS.medium,
              borderRadius: 0.5,
            }}
          />
          <Typography variant="caption">{t('risk.level.medium')}</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Box
            sx={{
              width: 14,
              height: 14,
              backgroundColor: MATRIX_COLORS.low,
              borderRadius: 0.5,
            }}
          />
          <Typography variant="caption">{t('risk.level.low')}</Typography>
        </Box>
      </Box>
    </Box>
  );
};

// =============================================================================
// CIA SCORE DISPLAY
// =============================================================================

interface CIAScoreDisplayProps {
  component: {
    confidentialityScore?: number;
    integrityScore?: number;
    availabilityScore?: number;
  };
}

const CIAScoreDisplay: React.FC<CIAScoreDisplayProps> = ({ component }) => {
  const { t } = useTranslation();

  if (
    !component.confidentialityScore &&
    !component.integrityScore &&
    !component.availabilityScore
  ) {
    return null;
  }

  const getScoreColor = (score: number) => {
    if (score >= 8) return 'error';
    if (score >= 5) return 'warning';
    return 'success';
  };

  return (
    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
      {component.confidentialityScore && (
        <Chip
          label={`${t('component.confidentialityScore')}: ${component.confidentialityScore}`}
          color={getScoreColor(component.confidentialityScore)}
          size="small"
          variant="outlined"
        />
      )}
      {component.integrityScore && (
        <Chip
          label={`${t('component.integrityScore')}: ${component.integrityScore}`}
          color={getScoreColor(component.integrityScore)}
          size="small"
          variant="outlined"
        />
      )}
      {component.availabilityScore && (
        <Chip
          label={`${t('component.availabilityScore')}: ${component.availabilityScore}`}
          color={getScoreColor(component.availabilityScore)}
          size="small"
          variant="outlined"
        />
      )}
    </Box>
  );
};

// =============================================================================
// HIERARCHICAL COMPONENT → RISK → MITIGATION VIEW
// =============================================================================

interface ComponentRiskHierarchyProps {
  components: ComponentWithRisks[];
}

const ComponentRiskHierarchy: React.FC<ComponentRiskHierarchyProps> = ({
  components,
}) => {
  const { t } = useTranslation();

  // Filter to only show components with risks
  const componentsWithRisksFiltered = components.filter(
    c => c.risks && c.risks.length > 0
  );

  if (componentsWithRisksFiltered.length === 0) {
    return (
      <Alert severity="info" sx={{ mt: 2 }}>
        {t('embed.noRisksFound')}
      </Alert>
    );
  }

  return (
    <Box>
      {componentsWithRisksFiltered.map(component => (
        <Accordion key={component.id} defaultExpanded sx={{ mb: 1 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="subtitle1" fontWeight="medium">
                {component.name}
              </Typography>
              <Chip
                label={`${component.risks?.length || 0} ${t('embed.risks')}`}
                size="small"
                color="warning"
                variant="outlined"
              />
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <CIAScoreDisplay component={component} />
            <Box sx={{ mt: 2 }}>
              {component.risks?.map(risk => (
                <RiskCard key={risk.id} risk={risk} />
              ))}
            </Box>
          </AccordionDetails>
        </Accordion>
      ))}
    </Box>
  );
};

// =============================================================================
// RISK CARD WITH MITIGATIONS
// =============================================================================

interface RiskCardProps {
  risk: RiskWithMitigations;
}

const RiskCard: React.FC<RiskCardProps> = ({ risk }) => {
  const { t, i18n } = useTranslation();

  // Standard best practice risk colors
  const RISK_COLORS = {
    critical: '#B71C1C', // Dark red
    high: '#E65100', // Orange
    medium: '#F9A825', // Yellow/amber
    low: '#2E7D32', // Green
    minimal: '#2E7D32', // Green
  };

  // Risk level thresholds matching risk.service.ts:
  // critical >= 8.0, high >= 6.0, medium >= 4.0, low >= 2.0, minimal < 2.0
  const getRiskColor = (score: number): string => {
    if (score >= 8.0) return RISK_COLORS.critical;
    if (score >= 6.0) return RISK_COLORS.high;
    if (score >= 4.0) return RISK_COLORS.medium;
    if (score >= 2.0) return RISK_COLORS.low;
    return RISK_COLORS.minimal;
  };

  // Get sx styles for risk chip with Avinor brand colors
  const getRiskChipSx = (score: number, variant: 'outlined' | 'filled') => {
    const color = getRiskColor(score);
    if (variant === 'filled') {
      return {
        backgroundColor: color,
        color: 'white',
      };
    } else {
      return {
        borderColor: color,
        color: color,
      };
    }
  };

  const getMitigationStatusIcon = (status: string) => {
    switch (status) {
      case 'implemented':
        return <ImplementedIcon fontSize="small" color="success" />;
      case 'verified':
        return <VerifiedIcon fontSize="small" color="success" />;
      case 'in_progress':
        return <InProgressIcon fontSize="small" color="warning" />;
      case 'planned':
      default:
        return <PlannedIcon fontSize="small" color="action" />;
    }
  };

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 2, backgroundColor: 'grey.50' }}>
      {/* Risk Header */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1 }}>
        <RiskIcon color="warning" sx={{ mt: 0.5 }} />
        <Box sx={{ flex: 1 }}>
          <Typography variant="subtitle2" fontWeight="medium">
            {risk.title}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {risk.description}
          </Typography>
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 0.5,
            alignItems: 'flex-end',
          }}
        >
          {/* Inherent Risk */}
          <Chip
            label={`${t('embed.inherentRisk')}: ${Number(risk.inherentRisk || 0).toFixed(1)}`}
            size="small"
            variant="outlined"
            sx={{
              borderRadius: 1,
              minWidth: 145,
              justifyContent: 'center',
              ...getRiskChipSx(Number(risk.inherentRisk || 0), 'outlined'),
            }}
          />
          {/* Residual Risk - prominent if exists */}
          {risk.residualRisk !== undefined && risk.residualRisk !== null && (
            <Chip
              label={`${t('embed.residualRisk')}: ${Number(risk.residualRisk).toFixed(1)}`}
              size="small"
              variant="filled"
              sx={{
                borderRadius: 1,
                minWidth: 145,
                justifyContent: 'center',
                fontWeight: 'bold',
                ...getRiskChipSx(Number(risk.residualRisk), 'filled'),
              }}
            />
          )}
        </Box>
      </Box>

      {/* Risk Details */}
      <Box sx={{ ml: 4, mb: 2 }}>
        {risk.threatSource && (
          <Typography variant="caption" display="block" color="text.secondary">
            <strong>{t('risk.fields.threatSource')}:</strong>{' '}
            {risk.threatSource}
          </Typography>
        )}
        {risk.vulnerability && (
          <Typography variant="caption" display="block" color="text.secondary">
            <strong>{t('risk.fields.vulnerability')}:</strong>{' '}
            {risk.vulnerability}
          </Typography>
        )}
        {/* MITRE ATT&CK Reference */}
        {risk.attackTechniqueId && (
          <Box sx={{ mt: 1 }}>
            {(() => {
              const attackUrl = getAttackUrl(risk.attackTechniqueId);
              // Get technique name from server-enriched data (from dynamic AttackDataService)
              const techniqueName = risk.attackTechniqueName;
              const tooltipText = techniqueName
                ? `${techniqueName} - ${t('embed.clickToLearnMore')}`
                : t('embed.viewAttackTechnique');
              return (
                <Tooltip title={tooltipText} arrow>
                  <Chip
                    label={`ATT&CK: ${risk.attackTechniqueId}${techniqueName ? ` - ${techniqueName}` : ''}`}
                    size="small"
                    variant="outlined"
                    color="error"
                    component="a"
                    href={attackUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    clickable
                  />
                </Tooltip>
              );
            })()}
          </Box>
        )}
      </Box>

      {/* Mitigations */}
      {risk.mitigations && risk.mitigations.length > 0 && (
        <Box sx={{ ml: 4, pl: 2, borderLeft: 2, borderColor: 'success.light' }}>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}
          >
            <MitigationIcon fontSize="small" />
            {t('embed.mitigations')} ({risk.mitigations.length})
          </Typography>
          {risk.mitigations.map(mitigation => (
            <Paper
              key={mitigation.id}
              variant="outlined"
              sx={{
                p: 1.5,
                mb: 1,
                backgroundColor: 'background.paper',
              }}
            >
              {/* Mitigation Header */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 1,
                  mb: 0.5,
                }}
              >
                {getMitigationStatusIcon(mitigation.implementationStatus)}
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" fontWeight="medium">
                    {mitigation.title}
                  </Typography>
                  {mitigation.description && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: 'block', mt: 0.5 }}
                    >
                      {mitigation.description}
                    </Typography>
                  )}
                </Box>
                {mitigation.reductionFactor && (
                  <Chip
                    label={`-${Math.round(mitigation.reductionFactor * 100)}%`}
                    size="small"
                    color="success"
                    variant="outlined"
                  />
                )}
              </Box>

              {/* Mitigation Badges */}
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  mt: 1,
                  ml: 3,
                }}
              >
                {/* Left side: Framework references (D3FEND, ISO) and due date */}
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {mitigation.d3fendId &&
                    (() => {
                      const d3fendUrl = getD3fendUrl(mitigation.d3fendId);
                      // Get technique name from server-enriched data (from dynamic D3fendDataService)
                      const techniqueName = mitigation.d3fendName;
                      return (
                        <Tooltip
                          title={
                            techniqueName
                              ? `${techniqueName} - ${t('embed.clickToLearnMore')}`
                              : t('embed.viewD3fendTechnique')
                          }
                          arrow
                        >
                          <Chip
                            label={`D3FEND: ${mitigation.d3fendId}${techniqueName ? ` - ${techniqueName}` : ''}`}
                            size="small"
                            variant="outlined"
                            color="success"
                            component="a"
                            href={d3fendUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            clickable
                          />
                        </Tooltip>
                      );
                    })()}
                  {mitigation.isoControlId &&
                    (() => {
                      const isoControl = ISO_CONTROLS.find(
                        c => c.id === mitigation.isoControlId
                      );
                      const isNorwegian =
                        i18n.language === 'nb' || i18n.language === 'no';
                      const controlName = isoControl
                        ? isNorwegian
                          ? isoControl.nameNb
                          : isoControl.name
                        : mitigation.isoControlId;
                      const controlDescription = isoControl
                        ? isNorwegian
                          ? isoControl.descriptionNb
                          : isoControl.description
                        : '';
                      return (
                        <Tooltip title={controlDescription} arrow>
                          <Chip
                            label={`ISO 27001:2022: ${mitigation.isoControlId}${isoControl ? ` - ${controlName}` : ''}`}
                            size="small"
                            variant="outlined"
                            color="primary"
                          />
                        </Tooltip>
                      );
                    })()}
                  {mitigation.dueDate &&
                    mitigation.implementationStatus !== 'verified' && (
                      <Chip
                        label={`${t('mitigation.fields.dueDate')}: ${new Date(mitigation.dueDate).toLocaleDateString()}`}
                        size="small"
                        variant="outlined"
                        color={
                          new Date(mitigation.dueDate) < new Date()
                            ? 'error'
                            : 'default'
                        }
                      />
                    )}
                </Box>
                {/* Right side: Status, Priority, Owner */}
                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 0.5,
                    justifyContent: 'flex-end',
                  }}
                >
                  <Chip
                    label={`${t('mitigation.fields.status')}: ${t(`mitigation.status.${mitigation.implementationStatus}`)}`}
                    size="small"
                    color={
                      mitigation.implementationStatus === 'verified'
                        ? 'primary'
                        : mitigation.implementationStatus === 'implemented'
                          ? 'info'
                          : mitigation.implementationStatus === 'in_progress'
                            ? 'warning'
                            : 'default'
                    }
                    variant="outlined"
                  />
                  {mitigation.priority &&
                    mitigation.implementationStatus !== 'verified' && (
                      <Chip
                        label={`${t('mitigation.fields.priority')}: ${t(`mitigation.priority.${mitigation.priority}`)}`}
                        size="small"
                        variant="outlined"
                        color={
                          mitigation.priority === 'high' ? 'warning' : 'default'
                        }
                      />
                    )}
                  {mitigation.owner && (
                    <Chip
                      label={`${t('mitigation.fields.owner')}: ${mitigation.owner}`}
                      size="small"
                      variant="outlined"
                    />
                  )}
                </Box>
              </Box>
            </Paper>
          ))}
        </Box>
      )}
    </Paper>
  );
};

export default EmbedView;
